/**
 * 送付の記録の一覧（#223 の2回目、docs/89 §6）。帳票の画面（「送付 n回 ›」）・{customer}／{vendor}の詳細・{project}の画面・左のメニュー「送付状」（会社の全件＝3回目の前の直し ★12）に。
 *   列：日付・送付先・型（FAX／窓付封筒／メール）・枚数・同封書類・送付者（「出した人」＝言葉の決まり #186 で「〜人」と書かない）。
 *   「表示」は送付の記録の PDF の画面 /deliveries/:id（2026-10-21 射場さんの決め＝★18。前は保存した PDF を新しいタブ。保存した PDF を作り直さないのは同じ）。
 *   未登録の送付先（★13）は送付先の後ろに「未登録」の札。
 *   記録は修正も削除もできない（画面にもその操作を置かない）。幅 767px 以下・スマホ版は行を2段のカードに（見るだけ）
 *   メールの行（#225、docs/91 §3.4）：「状態」の札＝送信済／未取得／取得済 MM/DD／届いていません（朱）／無効／期限切れ／送信失敗（朱）。印刷・FAX の行は空
 */
import { COVER_LETTER_MSG as M, MAIL_MSG, MAIL_STATE_LABEL, coverLineText, deliveryModeText, type DeliveryList, type DeliverySummary } from "@genbaryoku/contract";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api, messageOf } from "../api.js";
import { fmtDateTimeShort } from "../lib/dates.js";
import { useNarrow } from "../lib/useNarrow.js";
import { currentView } from "../lib/view.js";
import { ErrorText, Help } from "./ui.jsx";

export type DeliveryFilter = { document: string } | { rfq: string } | { partner: string } | { group: string };
const qsOf = (f: DeliveryFilter): string => {
  const [k, v] = Object.entries(f)[0]!;
  return `${k}=${encodeURIComponent(v as string)}`;
};

/** 一覧を読む（画面に入ったとき1回。印刷・保存のあとは呼ぶ側が invalidate する＝鍵の頭は "deliveries"） */
export function useDeliveries(f: DeliveryFilter | null) {
  const qs = f ? qsOf(f) : null;
  return useQuery({ queryKey: ["deliveries", qs], queryFn: () => api<DeliveryList>("GET", `/deliveries?${qs}`), enabled: !!qs, staleTime: 30_000, retry: false });
}

const toText = (d: DeliverySummary) => [d.partnerName, d.branchName].filter(Boolean).join(" ") || "—";
/** 送付先の欄（未登録の送付先は後ろに「未登録」の札） */
const ToCell = ({ d }: { d: DeliverySummary }) => (
  <>
    {toText(d)}
    {d.unregistered ? (
      <span className="chip dlv-un" data-testid="delivery-unregistered">
        {M.unregisteredMark}
      </span>
    ) : null}
  </>
);
const itemsText = (d: DeliverySummary) => d.items.map((i) => coverLineText(i)).join("、") || "—";
/** メールの行の状態の札（#225）。色だけで伝えない＝札の文字で。朱は届いていません・送信失敗だけ */
export function MailStateChip({ d }: { d: DeliverySummary }) {
  const m = d.mail;
  if (!m) return null;
  const at = m.state === "opened" && m.firstAccessAt ? ` ${mdOf(m.firstAccessAt)}` : "";
  const shu = m.state === "bounced" || m.state === "failed";
  return (
    <span className={"chip dlv-state" + (shu ? " is-shu" : "")} data-state={m.state} title={m.state === "opened" ? fmtDateTimeShort(m.firstAccessAt!) : undefined} data-testid="delivery-state">
      {MAIL_STATE_LABEL[m.state]}
      {at}
    </span>
  );
}
/** 「10/21」（日本時間） */
const mdOf = (iso: string) => {
  const t = new Date(Date.parse(iso) + 9 * 3600 * 1000);
  return `${t.getUTCMonth() + 1}/${t.getUTCDate()}`;
};

/** 一覧の表（PC）・カード（狭い幅とスマホ版） */
export function DeliveryTable({ items, testId = "delivery-table" }: { items: ReadonlyArray<DeliverySummary>; testId?: string }) {
  const narrow = useNarrow() || currentView() === "phone";
  // 「表示」は送付の記録の PDF の画面（★18。戻るボタンでここへ戻る）。PDF の無いメール（辞退の連絡＝#233 2a）も本文を見られる
  const openBtn = (d: DeliverySummary) =>
    d.pdfUrl || d.mail ? (
      <Link className="btn text sm" to={`/deliveries/${d.id}`} aria-label={`${fmtDateTimeShort(d.at)} の送付の${M.openPdf}`} data-testid="delivery-open">
        表示
      </Link>
    ) : null;
  if (!items.length) return <p className="hint" data-testid={`${testId}-empty`}>{M.deliveriesEmpty}</p>;
  if (narrow) {
    return (
      <ul className="dlvcards" data-testid={testId}>
        {items.map((d) => (
          <li key={d.id} data-testid="delivery-row">
            <div className="l1">
              <span className="num">{fmtDateTimeShort(d.at)}</span>
              <span className="chip">{deliveryModeText(d)}</span>
              <MailStateChip d={d} />
              <span className="num">{d.pageCount ? M.pagesUnit.replace("{n}", String(d.pageCount)) : ""}</span>
              <span className="sp" />
              {openBtn(d)}
            </div>
            <div className="l2">
              <ToCell d={d} />
            </div>
            <div className="l3">{itemsText(d)}</div>
            {d.by ? <div className="l3">{`${M.colBy}　${d.by}`}</div> : null}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <table className="dlvtable" data-testid={testId}>
      <thead>
        <tr>
          <th scope="col">{M.colDate}</th>
          <th scope="col">{M.colTo}</th>
          <th scope="col">{M.colMode}</th>
          <th scope="col">{MAIL_MSG.stateTitle}</th>
          <th scope="col" className="r">{M.colPages}</th>
          <th scope="col">{M.colItems}</th>
          <th scope="col">{M.colBy}</th>
          <th scope="col">
            <span className="sr">PDF</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id} data-testid="delivery-row">
            <td className="num nw">{fmtDateTimeShort(d.at)}</td>
            <td className="to" title={toText(d)}>
              <ToCell d={d} />
            </td>
            <td className="nw">{deliveryModeText(d)}</td>
            <td className="nw">
              <MailStateChip d={d} />
            </td>
            <td className="num r nw">{d.pageCount ? M.pagesUnit.replace("{n}", String(d.pageCount)) : "—"}</td>
            <td className="items">{itemsText(d)}</td>
            <td className="nw">{d.by ?? "—"}</td>
            <td className="nw">{openBtn(d)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** 窓（帳票の画面の「送付 n回 ›」から） */
export function DeliveriesDialog({ filter, onClose }: { filter: DeliveryFilter; onClose: () => void }) {
  const q = useDeliveries(filter);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg wide dlvdlg" role="dialog" aria-modal="true" aria-labelledby="dlv-h" data-testid="deliveries-dialog" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <div className="dh">
          <span id="dlv-h">{M.deliveriesTitle}</span>
          <Help label={`${M.deliveriesTitle}の説明`} text={M.deliveriesHelp} testId="help-deliveries" />
        </div>
        <div className="db">
          {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
          {q.data ? <DeliveryTable items={q.data.items} /> : !q.isError ? <p className="hint">{M.loading}</p> : null}
        </div>
        <div className="df">
          <span className="sp" />
          <button type="button" className="btn quiet" autoFocus onClick={onClose} data-testid="deliveries-close">
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}

/** 「送付 n回 ›」（1回もなければ出さない＝届く前も幅を取らない）。押すと窓 */
export function DeliveriesLink({ filter, className = "esrfq", testId = "deliveries-link" }: { filter: DeliveryFilter | null; className?: string; testId?: string }) {
  const q = useDeliveries(filter);
  const [open, setOpen] = useState(false);
  const n = q.data?.items.length ?? 0;
  if (!filter || !n) return null;
  return (
    <>
      <button type="button" className={`linkbtn ${className}`} onClick={() => setOpen(true)} data-testid={testId}>
        {M.deliveriesCount.replace("{n}", String(n))} ›
      </button>
      {open ? <DeliveriesDialog filter={filter} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** 節（{customer}の詳細・{project}の画面）：題と表。1回もなければ「送付の記録はありません」 */
export function DeliveriesSection({ filter, className = "paper", testId = "deliveries-section" }: { filter: DeliveryFilter; className?: string; testId?: string }) {
  const q = useDeliveries(filter);
  return (
    <section className={`${className} dlvsec`} data-testid={testId}>
      <div className="dlvhead">
        <h2>
          {M.deliveriesTitle}
          <Help label={`${M.deliveriesTitle}の説明`} text={M.deliveriesHelp} testId={`help-${testId}`} />
        </h2>
        {q.data?.items.length ? <span className="hint num">{M.deliveriesCount.replace("{n}", String(q.data.items.length))}</span> : null}
      </div>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {q.data ? <DeliveryTable items={q.data.items} /> : !q.isError ? <p className="hint">{M.loading}</p> : null}
    </section>
  );
}

/**
 * 会社の全件（左のメニュー「送付状」＝★12）：新しい順に 100 件ずつ。続きは「さらに表示」（GET /deliveries?before=<next>）
 */
export function DeliveriesAll({ testId = "deliveries-all", attention = false }: { testId?: string; /** 要確認だけ（#225） */ attention?: boolean }) {
  const q = useInfiniteQuery({
    queryKey: ["deliveries", "all", attention ? "attention" : "all"],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams();
      if (pageParam) qs.set("before", pageParam);
      if (attention) qs.set("state", "attention");
      const s = qs.toString();
      return api<DeliveryList>("GET", `/deliveries${s ? `?${s}` : ""}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next ?? null,
    staleTime: 30_000,
    retry: false,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <section className="paper dlvsec" data-testid={testId}>
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {q.data ? <DeliveryTable items={items} testId="delivery-table" /> : !q.isError ? <p className="hint">{M.loading}</p> : null}
      {q.hasNextPage ? (
        <p className="dlvmore">
          <button type="button" className="btn" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()} data-testid="deliveries-more">
            {q.isFetchingNextPage ? M.loading : M.listMore}
          </button>
        </p>
      ) : null}
    </section>
  );
}
